import {
  Box,
  Button,
  Divider,
  Fade,
  Popover,
  Tooltip,
  withStyles,
} from '@material-ui/core';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import {
  applyFilters,
  buildFilterParamsState,
  cleanFilters,
  fetchShopifyInventory,
  updateCheckboxFilter,
  updatePriceRange,
} from '@src/reducers/shopify';
import {
  FilterKeys,
  FilterParams,
  FilterParamsItem,
  HandleUpdateCheckboxFilter,
} from '@src/reducers/shopify/types';
import {
  getFilterParamsEditing,
  getShopifyCurrentStoreShowAvailabilityFilter,
  getShowOnlyAvailableProductsFilterValue,
} from '@src/selectors/shopify';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import ExtraFilters from './ExtraFilters';
import { FilterIcon, RemoveIcon } from './SvgIcons';
import { FilterOption, RangeOption } from './Utils';

const SIZE_FILTER_ITEM_IN_PX = 30;

type ItemView = {
  itemKey: string;
  key: string;
} & FilterParamsItem;

type SearchPatternState = {
  [key: string]: string;
};

export interface IFilter {
  filtersEditing: FilterParams[];
  showOnlyAvailableProducts: boolean;
  showOnlyAvailableProductsFilterValue: boolean;
  handleClickOnCheckbox: (value: HandleUpdateCheckboxFilter) => void;
  handleClickOnSubmit: () => void;
  handleOnClick: () => void;
  handleCleanFilter: () => void;
  handleUpdatePriceRange: (value: number[]) => void;
}

const StyledPopover = withStyles({
  paper: {
    boxShadow: 'inset 0 0 0 1px white, 0px 2px 8px 3px #00000014',
  },
})(Popover);

export const ButtonStyled = withStyles({
  root: {
    'min-width': '55px',
  },
})(Button);

const FilterWrapper: React.FC = () => {
  const dispatch = useDispatch();
  const filtersEditing = useSelector(getFilterParamsEditing);
  const showAvailabilityFilter = useSelector(
    getShopifyCurrentStoreShowAvailabilityFilter
  );
  const showOnlyAvailableProductsFilterValue = useSelector(
    getShowOnlyAvailableProductsFilterValue
  );

  const handleClickOnCheckbox = ({
    key,
    label,
    checked,
    type,
  }: HandleUpdateCheckboxFilter) => {
    dispatch(
      updateCheckboxFilter({
        key,
        label,
        checked,
        type,
      })
    );
  };

  const handleClickOnSubmit = () => {
    dispatch(applyFilters());
    dispatch(fetchShopifyInventory());
  };

  const handleOnClick = () => {
    dispatch(buildFilterParamsState());
  };

  const handleCleanFilter = () => {
    dispatch(cleanFilters());
    dispatch(fetchShopifyInventory());
  };

  const handleUpdatePriceRange = (value: number[]) => {
    dispatch(updatePriceRange(value));
  };

  return (
    <Filter
      filtersEditing={filtersEditing}
      showOnlyAvailableProducts={!showAvailabilityFilter}
      showOnlyAvailableProductsFilterValue={
        showOnlyAvailableProductsFilterValue
      }
      handleClickOnCheckbox={handleClickOnCheckbox}
      handleClickOnSubmit={handleClickOnSubmit}
      handleOnClick={handleOnClick}
      handleCleanFilter={handleCleanFilter}
      handleUpdatePriceRange={handleUpdatePriceRange}
    />
  );
};

export const Filter: React.FC<IFilter> = ({
  filtersEditing,
  showOnlyAvailableProducts,
  showOnlyAvailableProductsFilterValue,
  handleClickOnCheckbox,
  handleClickOnSubmit,
  handleOnClick,
  handleCleanFilter,
  handleUpdatePriceRange,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;

  const anchorRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchPattern, setSearchPattern] = useState<SearchPatternState>({});

  const itemView = ({
    key,
    itemKey,
    type,
    label,
    checked,
    value = [0, 0],
    max = 0,
  }: ItemView) => {
    if (type === 'range')
      return (
        <Box marginBottom={1} key={label}>
          <RangeOption
            value={value}
            max={max}
            handleChange={handleUpdatePriceRange}
          />
        </Box>
      );

    return (
      <FilterOption
        key={itemKey}
        handleClick={checkedValue =>
          handleClickOnCheckbox({
            key,
            label,
            checked: checkedValue,
            type,
          })
        }
        checked={!!checked}
        label={label}
        type={type}
      />
    );
  };
  const sectionsView = filtersEditing
    .filter(({ key }) => key !== FilterKeys.SHOW_ONLY_AVAILABLE_PRODUCTS)
    .map(({ key, items }) => {
      if (!items.length) return [];
      const labelPriceRange = formatMessage({ id: 'Shopify.Filter.prices' });
      const style: CSSProperties = {
        overflow: 'auto',
        height: `${Math.min(
          SIZE_FILTER_ITEM_IN_PX * items.length,
          SIZE_FILTER_ITEM_IN_PX * 4
        )}px`,
      };

      const filteredItems = items
        .filter(item =>
          item.label.toLocaleLowerCase().includes(searchPattern[key])
        )
        .sort((a, b) => {
          if (!a.checked && !b.checked) return 0;
          if (!a.checked) return 1;
          return -1;
        })
        .map((item, index) =>
          itemView({
            ...item,
            key,
            itemKey: `${item.label}-${index}`,
          } as ItemView)
        );

      return (
        <Box key={key}>
          <Divider />
          <Box display="flex" alignItems="center">
            <Box
              textAlign="center"
              fontSize={14}
              padding={1}
              color="text.primary"
              style={{ textTransform: 'capitalize' }}
              flexGrow={2}
            >
              {key === FilterKeys.PRICES ? labelPriceRange : key}
            </Box>

            {key !== FilterKeys.PRICES && (
              <Box position="relative">
                <input
                  placeholder={formatMessage({
                    id: 'Shopify.Filter.searchPattern.label',
                  })}
                  value={searchPattern[key] || ''}
                  onChange={({ target }) =>
                    setSearchPattern(state => ({
                      ...state,
                      [key]: target.value.toLocaleLowerCase(),
                    }))
                  }
                  style={{
                    fontSize: '10px',
                    padding: '0px 7px',
                    height: '16px',
                    border: '1px solid #D4D4D8',
                    borderRadius: '8px',
                    maxWidth: '88px',
                  }}
                />
                <Box
                  position="absolute"
                  right={6}
                  top={1}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setSearchPattern(state => ({
                      ...state,
                      [key]: '',
                    }));
                  }}
                >
                  {RemoveIcon}
                </Box>
              </Box>
            )}
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            // @ts-expect-error MUI v4 types incompatible with React 18, will be fixed in MUI v5 migration
            style={key !== FilterKeys.PRICES ? style : {}}
          >
            {filteredItems.length ? (
              filteredItems
            ) : (
              <Box textAlign="center">
                {formatMessage({ id: 'Shopify.Filter.search.no.results' })}
              </Box>
            )}
          </Box>
        </Box>
      );
    });

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    handleOnClick();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const submit = () => {
    handleClose();
    handleClickOnSubmit();
  };

  useEffect(() => {
    const obj: SearchPatternState = {};
    filtersEditing.forEach(item => {
      obj[item.key] = searchPattern[item.key] || '';
    });
    setSearchPattern(obj);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersEditing]);

  return (
    <>
      <Tooltip
        title={formatMessage({
          id: 'Shopify.Filter.button.tooltip',
        })}
      >
        <ButtonStyled
          ref={anchorRef}
          variant="outlined"
          disableElevation
          style={{
            padding: 8,
            fontSize: '14px',
            lineHeight: '100%',
            height: '32px',
            color: 'inherit',
          }}
          onClick={handleClick}
        >
          {FilterIcon}
        </ButtonStyled>
      </Tooltip>
      <StyledPopover
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        elevation={0}
        TransitionComponent={Fade}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box width="256px" padding={1}>
          <Box display="flex" justifyContent="center" width="100%">
            <Box flexGrow={3} marginLeft="73px">
              {formatMessage({ id: 'Shopify.Filter.title' })}
            </Box>
            <Box flexGrow={1} textAlign="right">
              {FilterIcon}
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" padding={1}>
            {showOnlyAvailableProducts && (
              <ExtraFilters
                showOnlyAvailableProductsFilterValue={
                  showOnlyAvailableProductsFilterValue
                }
                handleClickOnCheckbox={handleClickOnCheckbox}
              />
            )}
            {sectionsView}
            <Divider />
          </Box>
          <Box>
            <Box display="flex" justifyContent="space-between" padding="8px">
              <Button
                color="primary"
                variant="outlined"
                style={{ textTransform: 'capitalize', width: '88px' }}
                size="small"
                onClick={() => handleCleanFilter()}
                data-testid="shopify-filter-clean-button"
              >
                {formatMessage({ id: 'Shopify.Filter.clean.button.label' })}
              </Button>

              <Button
                color="primary"
                variant="contained"
                style={{ textTransform: 'capitalize', width: '88px' }}
                size="small"
                onClick={submit}
                data-testid="shopify-filter-apply-button"
              >
                {formatMessage({ id: 'Shopify.Filter.submit.button.label' })}
              </Button>
            </Box>
          </Box>
        </Box>
      </StyledPopover>
    </>
  );
};

export default FilterWrapper;
